import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, FileCheck, FileText, TrendingUp } from "lucide-react";
import { PageHero, StatusBadge, LoadingState, ErrorState } from "@/components/PageHeader";
import { Button, Input, Select } from "@/components/ui/form";
import { Card, CardBody, CardHeader, DataTable, DataTableHead, DataTableTd, DataTableTh, StatCard } from "@/components/ui/card";
import { useAuth, canWrite } from "@/state/AuthContext";
import { apiFetch } from "@/utils/api";
import { STATUS_LABELS } from "@/lib/utils";

interface RfqRow {
  id: string;
  rfq_number: string;
  status: string;
  customer_name: string;
  contact_name: string;
  created_by_name: string;
  winning_seller_name: string;
  requested_delivery_date: string;
  updated_at: string;
}

export function DashboardPage() {
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState<RfqRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    const data = await apiFetch<{ rfqs: RfqRow[] }>(`/rfqs${qs}`);
    setRfqs(data.rfqs);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [status]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const filtered = rfqs.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.rfq_number.toLowerCase().includes(q) ||
      r.customer_name.toLowerCase().includes(q) ||
      (r.contact_name ?? "").toLowerCase().includes(q)
    );
  });

  const openCount = rfqs.filter((r) => r.status !== "closed" && r.status !== "cancelled").length;
  const quotesCount = rfqs.filter((r) => r.status === "quotes_received").length;
  const poCount = rfqs.filter((r) => r.status === "po_issued" || r.status === "closed").length;

  return (
    <div>
      <PageHero
        title="RFQ Dashboard"
        subtitle="Track quote requests from creation through purchase order."
        breadcrumbs={[{ label: "Pages", to: "/" }, { label: "Dashboard" }]}
        actions={
          canWrite(user) ? (
            <Link to="/rfqs/new">
              <Button type="button">New RFQ</Button>
            </Link>
          ) : undefined
        }
      />

      <div className="relative -mt-20 z-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4 mb-6">
        <StatCard
          title="Total RFQs"
          value={rfqs.length}
          change={`${openCount} open`}
          changeType="neutral"
          icon={<FileText className="h-5 w-5" />}
        />
        <StatCard
          title="Active Pipeline"
          value={openCount}
          change={rfqs.length ? `${Math.round((openCount / rfqs.length) * 100)}% of total` : "—"}
          changeType="up"
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <StatCard
          title="Quotes Received"
          value={quotesCount}
          change="Awaiting approval"
          changeType="neutral"
          icon={<ClipboardList className="h-5 w-5" />}
        />
        <StatCard
          title="POs Issued"
          value={poCount}
          change="Completed or in progress"
          changeType="up"
          icon={<FileCheck className="h-5 w-5" />}
        />
      </div>

      <Card>
        <CardHeader title="Request for Quotes" subtitle="Filter and search all RFQs in the pipeline." />
        <CardBody className="pt-4">
          <div className="flex flex-wrap gap-3 mb-5">
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="max-w-xs">
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </Select>
            <Input
              placeholder="Search RFQ # or customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs"
            />
          </div>
          <DataTable>
            <DataTableHead>
              <DataTableTh>RFQ #</DataTableTh>
              <DataTableTh>Status</DataTableTh>
              <DataTableTh>Customer</DataTableTh>
              <DataTableTh>Requested By</DataTableTh>
              <DataTableTh>Created By</DataTableTh>
              <DataTableTh>Delivery</DataTableTh>
              <DataTableTh>Seller</DataTableTh>
            </DataTableHead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-argon-secondary">
                    No RFQs found.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-t border-gray-50 hover:bg-gray-50/80 transition-colors">
                    <DataTableTd>
                      <Link to={`/rfqs/${r.id}`} className="font-semibold text-argon-primary hover:underline">
                        {r.rfq_number}
                      </Link>
                    </DataTableTd>
                    <DataTableTd><StatusBadge status={r.status} /></DataTableTd>
                    <DataTableTd>{r.customer_name}</DataTableTd>
                    <DataTableTd>{r.contact_name || "—"}</DataTableTd>
                    <DataTableTd>{r.created_by_name}</DataTableTd>
                    <DataTableTd>{r.requested_delivery_date || "—"}</DataTableTd>
                    <DataTableTd>{r.winning_seller_name || "—"}</DataTableTd>
                  </tr>
                ))
              )}
            </tbody>
          </DataTable>
        </CardBody>
      </Card>
    </div>
  );
}
